import React, { useState, useEffect, useMemo } from 'react';
import { X, Calendar, MapPin, Eye, FileSpreadsheet, FileText } from 'lucide-react';
import { formCompletionService, FormCompletion } from '@/firebase/formCompletionService';
import { locationService, Location } from '@/firebase/locationService';
import { assetService } from '@/firebase/assetService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { getUserPreferredLanguage, getFormName, getFieldLabelMapping } from '@/utils/formLanguageHelper';
import { Form } from '@/firebase/formService';
import { evaluateFormula, type FormulaContext } from '@/utils/formulaEvaluator';
import jsPDF from 'jspdf';

interface FormHistoryProps {
  form: Form;
  onClose: () => void;
}

const FormHistory: React.FC<FormHistoryProps> = ({ form, onClose }) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const preferredLanguage = getUserPreferredLanguage(user);
  const [completions, setCompletions] = useState<FormCompletion[]>([]);
  const [locations, setLocations] = useState<Record<string, Location>>({});
  const [assetNames, setAssetNames] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [userLocationIds, setUserLocationIds] = useState<string[]>([]);
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);
  
  // Set default to current month (first day of month to today)
  const getCurrentMonthStart = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    return new Date(year, month, 1);
  };
  
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [startDate, setStartDate] = useState<string>(formatDateForInput(getCurrentMonthStart()));
  const [endDate, setEndDate] = useState<string>(formatDateForInput(new Date()));
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [filterNonZero, setFilterNonZero] = useState<boolean>(false);
  const [filterColumn, setFilterColumn] = useState<string>('');

  useEffect(() => {
    loadUserLocations();
  }, [user?.id, currentOrganization?.id]);

  useEffect(() => {
    loadCompletions();
    loadLocations();
  }, [form.id, currentOrganization?.id, userLocationIds.length, isSystemUser]);

  const loadUserLocations = async () => {
    if (user?.id && currentOrganization?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const { fetchUserRoles, isSystemUserInOrganization } = await import('@/utils/roleHelpers');
        
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const locationIds = userData.locationIds || [];
          setUserLocationIds(locationIds);
          
          // Use organization-specific role check
          if (currentOrganization?.id) {
            setIsSystemUser(isSystemUserInOrganization(userData, currentOrganization.id));
          } else {
            // Fallback: check if user is a system user using legacy method
            const roles = await fetchUserRoles(user.id);
            setIsSystemUser(roles.isSystemUser);
          }
        }
      } catch (error) {
        console.error('Error loading user locations:', error);
        setUserLocationIds([]);
      }
    } else {
      setUserLocationIds([]);
    }
  };

  const loadLocations = async () => {
    if (!currentOrganization?.id) return;
    
    try {
      let userRole: string | undefined;
      if (user?.id) {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const userRef = doc(db, 'users', user.id);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            userRole = userData.role;
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
      
      // For system users, only load accessible locations
      const result = isSystemUser && userLocationIds.length > 0
        ? await locationService.getAccessibleLocations(currentOrganization.id, user?.id, userRole, userLocationIds)
        : await locationService.getAllLocations(currentOrganization.id);
      
      if (result.success && result.data) {
        const locationsMap: Record<string, Location> = {};
        result.data.forEach((loc: Location) => {
          if (loc.id) {
            locationsMap[loc.id] = loc;
          }
        });
        setLocations(locationsMap);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadCompletions = async () => {
    if (!form.id) return;
    
    setIsLoading(true);
    try {
      const result = await formCompletionService.getCompletionsForForm(form.id);
      if (result.success && result.data) {
        let allCompletions = result.data;
        
        // Filter completions by accessible locations for system users
        if (isSystemUser && userLocationIds.length > 0) {
          allCompletions = allCompletions.filter(completion => 
            completion.locationId && userLocationIds.includes(completion.locationId)
          );
        }
        
        setCompletions(allCompletions);
        
        // Fetch asset names for completions that have assetId (use filtered completions)
        const assetIdsToFetch = new Set<string>();
        allCompletions.forEach(completion => {
          if (completion.assetId) {
            assetIdsToFetch.add(completion.assetId);
          }
        });
        
        // Fetch asset names in parallel
        if (assetIdsToFetch.size > 0) {
          const assetNamePromises = Array.from(assetIdsToFetch).map(async (assetId) => {
            try {
              const assetResult = await assetService.getAsset(assetId);
              if (assetResult.success && assetResult.data) {
                return { assetId, assetName: assetResult.data.assetName || '' };
              }
            } catch (error) {
              console.error(`Error fetching asset ${assetId}:`, error);
            }
            return null;
          });
          
          const assetNameResults = await Promise.all(assetNamePromises);
          const assetNamesMap: Record<string, string> = {};
          assetNameResults.forEach(result => {
            if (result) {
              assetNamesMap[result.assetId] = result.assetName;
            }
          });
          
          setAssetNames(assetNamesMap);
        }
      }
    } catch (error) {
      console.error('Error loading completions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to convert Firestore timestamp to Date
  const getDateFromValue = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (value?.toDate && typeof value.toDate === 'function') return value.toDate();
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') return new Date(value);
    return null;
  };

  // Helper function to calculate calculated fields for a table item
  // This must be defined before filteredCompletions useMemo since it's used there
  const calculateItemFields = (item: { item: string; fieldValues?: Record<string, any> }): Record<string, any> => {
    const itemFieldValues = { ...(item.fieldValues || {}) };
    
    // Include per-item reference values
    form.tableFields?.forEach(field => {
      if (field.type === 'reference') {
        const refValue = field.itemReferenceValues?.[item.item];
        if (refValue !== undefined) {
          itemFieldValues[field.label] = refValue;
        }
      }
    });
    
    // Calculate calculated fields
    const calculatedFields = form.tableFields?.filter(f => f.type === 'calculated' && f.formula) || [];
    calculatedFields.forEach(field => {
      if (field.formula) {
        try {
          const context: FormulaContext = {
            fieldValues: itemFieldValues,
            fields: form.tableFields || []
          };
          const result = evaluateFormula(field.formula, context);
          itemFieldValues[field.label] = result;
        } catch (error: any) {
          console.error(`Error calculating field "${field.label}" for item "${item.item}":`, error);
          itemFieldValues[field.label] = `Error: ${error.message}`;
        }
      }
    });
    
    return itemFieldValues;
  };

  // Filter completions based on date range and location
  const filteredCompletions = useMemo(() => {
    const isTableForm = form.layoutType === 'table' || completions.some(c => c.isTableForm);
    
    return completions.filter(completion => {
      // Filter by location dropdown selection
      if (selectedLocationId && completion.locationId !== selectedLocationId) {
        return false;
      }
      
      // For system users, ensure only accessible locations are shown even when no location is selected
      if (isSystemUser && userLocationIds.length > 0) {
        if (!completion.locationId || !userLocationIds.includes(completion.locationId)) {
          return false;
        }
      }

      // Filter by date range
      const completionDate = getDateFromValue(completion.completionDate);
      if (!completionDate) return false;

      if (startDate) {
        // Parse start date and set to beginning of day in local timezone
        const start = new Date(startDate + 'T00:00:00');
        // Normalize completion date to beginning of day for comparison
        const completionStartOfDay = new Date(completionDate);
        completionStartOfDay.setHours(0, 0, 0, 0);
        // Include records on or after start date
        if (completionStartOfDay < start) return false;
      }

      if (endDate) {
        // Parse end date and set to end of day in local timezone
        const end = new Date(endDate + 'T23:59:59.999');
        // Include records on or before end date (compare full timestamp)
        if (completionDate > end) return false;
      }

      // For table forms, filter out rows with N/A or empty values in selected column
      if (isTableForm && filterNonZero && filterColumn && completion.tableItems) {
        // Check if at least one item in this completion has a non-zero/non-N/A value in the selected column
        const hasValidData = completion.tableItems.some(item => {
          const calculatedFields = calculateItemFields(item);
          const value = calculatedFields[filterColumn];
          
          // Check if value is N/A, empty, null, undefined, or zero (for numeric values)
          if (value === null || value === undefined || value === '' || value === 'N/A' || value === 'N/A') {
            return false;
          }
          
          // For numeric values, also exclude zero
          const numValue = Number(value);
          if (!isNaN(numValue) && numValue === 0) {
            return false;
          }
          
          return true;
        });
        
        // If filtering is enabled and no items have valid data, exclude this completion
        if (!hasValidData) {
          return false;
        }
      }

      return true;
    });
  }, [completions, startDate, endDate, selectedLocationId, isSystemUser, userLocationIds, form.layoutType, filterNonZero, filterColumn]);

  // Get unique locations from completions with their names
  const availableLocations = useMemo(() => {
    const locationMap = new Map<string, { id: string; name: string }>();
    completions.forEach(completion => {
      if (completion.locationId) {
        // For system users, only include locations they have access to
        if (isSystemUser && userLocationIds.length > 0) {
          if (!userLocationIds.includes(completion.locationId)) {
            return; // Skip this location
          }
        }
        
        // Check if we already have this location
        if (!locationMap.has(completion.locationId)) {
          const location = locations[completion.locationId];
          const locationName = location?.name || completion.locationName || completion.locationId;
          locationMap.set(completion.locationId, {
            id: completion.locationId,
            name: locationName,
          });
        }
      }
    });
    // Sort by name for better UX
    return Array.from(locationMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [completions, locations, isSystemUser, userLocationIds]);

  // Auto-select location filter if user has access to only one location
  useEffect(() => {
    if (availableLocations.length === 1 && !selectedLocationId && availableLocations[0]?.id) {
      setSelectedLocationId(availableLocations[0].id);
    }
  }, [availableLocations, selectedLocationId]);

  const formatDate = (date: Date | null): string => {
    if (!date) return 'N/A';
    // Ensure we're working with a Date object in local timezone
    const localDate = date instanceof Date ? date : new Date(date);
    return localDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short', // This will show timezone (e.g., EST, PST)
    });
  };

  const handleExportExcel = () => {
    if (filteredCompletions.length === 0) {
      alert('No data to export');
      return;
    }

    // Check if this is a table form
    const isTableForm = form.layoutType === 'table' || filteredCompletions.some(c => c.isTableForm);

    if (isTableForm) {
      // TABLE FORM EXPORT: Items as rows, with Date column
      handleTableFormExport(filteredCompletions, filterNonZero, filterColumn);
      return;
    }

    // STANDARD FORM EXPORT: Original logic
    // Get field labels in the order they appear in the form
    const formFieldLabels: string[] = [];
    if (form.fields && form.fields.length > 0) {
      // Sort by order field if available, otherwise use array order
      const sortedFields = [...form.fields].sort((a, b) => {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        return orderA - orderB;
      });
      formFieldLabels.push(...sortedFields.map(field => field.label));
    }

    // Collect any additional field labels from completions that might not be in form definition
    const allFieldLabels = new Set<string>(formFieldLabels);
    filteredCompletions.forEach(completion => {
      if (completion.fieldValues) {
        Object.keys(completion.fieldValues).forEach(label => {
          allFieldLabels.add(label);
        });
      }
    });

    // Create ordered field labels: form fields first (in order), then any extras
    const orderedFieldLabels = [
      ...formFieldLabels,
      ...Array.from(allFieldLabels).filter(label => !formFieldLabels.includes(label))
    ];

    // Create headers: only form field labels
    const headers = orderedFieldLabels;

    // Helper to format date values - convert all date formats to consistent readable format
    const formatDateValue = (value: any): string => {
      if (!value) return '';
      
      if (typeof value === 'string') {
        // Check if it's a date string (plain YYYY-MM-DD or ISO timestamp)
        const isPlainDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
        const isIsoTimestamp = value.includes('T') || value.includes('Z');
        
        if (isPlainDate || isIsoTimestamp) {
          try {
            // Parse the date - for plain dates, append time to avoid timezone issues
            const dateString = isPlainDate ? `${value}T12:00:00` : value;
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
              // Convert to consistent readable format
              return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });
            }
          } catch (e) {
            // If parsing fails, return as is
          }
        }
      }
      
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      return String(value);
    };

    // Helper to format field value for CSV
    const formatFieldValue = (value: any): string => {
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'string') {
        // Check if it's a date string first
        const dateFormatted = formatDateValue(value);
        if (dateFormatted !== String(value)) {
          return dateFormatted;
        }
        // If it's a URL (photo), just return the URL
        if (value.startsWith('http://') || value.startsWith('https://')) {
          return value;
        }
        return value;
      }
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      if (typeof value === 'object') {
        // For objects/arrays, stringify but keep it readable
        return JSON.stringify(value);
      }
      return String(value);
    };

    // Create rows
    const rows = filteredCompletions.map(completion => {
      const completionDate = getDateFromValue(completion.completionDate);
      const location = completion.locationId ? locations[completion.locationId] : null;
      const locationName = location ? location.name : completion.locationName || 'N/A';
      const assetName = completion.assetId ? assetNames[completion.assetId] : '';
      
      // Build row: only field values
      const row: string[] = [];

      // Add field values in the same order as headers
      orderedFieldLabels.forEach(label => {
        const value = completion.fieldValues?.[label];
        row.push(formatFieldValue(value));
      });

      return row;
    });

    // Escape CSV values
    const escapeCsv = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Build CSV content
    const csvContent = [
      headers.map(escapeCsv).join(','),
      ...rows.map(row => row.map(cell => escapeCsv(String(cell))).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const displayFormName = getFormName(form, preferredLanguage);
    link.setAttribute('download', `${displayFormName || 'form'}_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Helper function for table form exports
  const handleTableFormExport = (completions: FormCompletion[], applyFilter: boolean = false, filterColumnName: string = '') => {
    // Escape CSV values helper
    const escapeCsv = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Helper to format date values
    const formatDateValue = (value: any): string => {
      if (!value) return '';
      
      if (typeof value === 'string') {
        const isPlainDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
        const isIsoTimestamp = value.includes('T') || value.includes('Z');
        
        if (isPlainDate || isIsoTimestamp) {
          try {
            const dateString = isPlainDate ? `${value}T12:00:00` : value;
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
              return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });
            }
          } catch (e) {
            // If parsing fails, return as is
          }
        }
      }
      
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      return String(value);
    };

    // Helper to format field value for CSV
    const formatFieldValue = (value: any): string => {
      if (value === null || value === undefined) {
        return '';
      }
      
      // Handle Firestore Timestamp objects
      if (value.toDate && typeof value.toDate === 'function') {
        try {
          const date = value.toDate();
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        } catch (e) {
          // If conversion fails, fall through
        }
      }
      
      // Handle Firestore Timestamp with seconds property
      if (value.seconds && typeof value.seconds === 'number') {
        try {
          const date = new Date(value.seconds * 1000);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        } catch (e) {
          // If conversion fails, fall through
        }
      }
      
      if (typeof value === 'string') {
        const dateFormatted = formatDateValue(value);
        if (dateFormatted !== String(value)) {
          return dateFormatted;
        }
        if (value.startsWith('http://') || value.startsWith('https://')) {
          return value;
        }
        return value;
      }
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      if (typeof value === 'object') {
        // Don't stringify Firestore Timestamps, skip them
        if (value.seconds !== undefined || value.toDate !== undefined) {
          return formatDateValue(value);
        }
        return JSON.stringify(value);
      }
      return String(value);
    };

    // Get header field labels (like Date)
    const headerFieldLabels: string[] = [];
    // Header fields removed - use variables instead

    // Get table field labels (columns) in order
    const tableFieldLabels: string[] = [];
    if (form.tableFields && form.tableFields.length > 0) {
      const sortedTableFields = [...form.tableFields].sort((a, b) => {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        return orderA - orderB;
      });
      tableFieldLabels.push(...sortedTableFields.map(field => field.label));
    }

    // Collect all table field labels from completions (including calculated fields)
    const allTableFieldLabels = new Set<string>(tableFieldLabels);
    completions.forEach(completion => {
      if (completion.tableItems) {
        completion.tableItems.forEach(item => {
          const calculatedFields = calculateItemFields(item);
          Object.keys(calculatedFields).forEach(label => {
            allTableFieldLabels.add(label);
          });
        });
      }
    });

    // Create ordered headers: Date (from header), Item, then table fields
    const orderedTableFieldLabels = [
      ...tableFieldLabels,
      ...Array.from(allTableFieldLabels).filter(label => !tableFieldLabels.includes(label))
    ];

    // Find Date field label (common header field)
    const dateFieldLabel = headerFieldLabels.find(label => 
      label.toLowerCase().includes('date') || label.toLowerCase() === 'date'
    ) || (headerFieldLabels.length > 0 ? headerFieldLabels[0] : 'Date');

    // Build headers: Date, Item, then table fields
    const headers = [
      dateFieldLabel, // Date column
      'Item', // Item name column
      ...orderedTableFieldLabels // Table field columns
    ];

    // Build rows: each tableItem becomes a row
    const rows: string[] = [];
    completions.forEach(completion => {
      // Get date from completionDate - use getDateFromValue helper for proper Firestore Timestamp handling
      const completionDateObj = getDateFromValue(completion.completionDate);
      const dateValue = completionDateObj ? completionDateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : '';

      if (completion.tableItems && completion.tableItems.length > 0) {
        completion.tableItems.forEach(tableItem => {
          // Apply filter if enabled: skip rows with N/A or empty values in selected column
          if (applyFilter && filterColumnName) {
            const calculatedFields = calculateItemFields(tableItem);
            const value = calculatedFields[filterColumnName];
            
            // Skip if value is N/A, empty, null, undefined, or zero (for numeric values)
            if (value === null || value === undefined || value === '' || value === 'N/A' || String(value).toUpperCase() === 'N/A') {
              return;
            }
            
            // For numeric values, also exclude zero
            const numValue = Number(value);
            if (!isNaN(numValue) && numValue === 0) {
              return;
            }
          }
          
          const row: string[] = [];
          
          // Date column
          row.push(formatFieldValue(dateValue));
          
          // Item column
          row.push(tableItem.item || '');
          
          // Table field columns (including calculated fields)
          const calculatedFields = calculateItemFields(tableItem);
          orderedTableFieldLabels.forEach(fieldLabel => {
            const value = calculatedFields[fieldLabel];
            row.push(formatFieldValue(value));
          });
          
          rows.push(row.map(cell => escapeCsv(String(cell))).join(','));
        });
      }
    });

    // Build CSV content
    const csvContent = [
      headers.map(escapeCsv).join(','),
      ...rows
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const displayFormName = getFormName(form, preferredLanguage);
    link.setAttribute('download', `${displayFormName || 'form'}_table_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    if (filteredCompletions.length === 0) {
      alert('No data to export');
      return;
    }

    // Check if this is a table form
    const isTableForm = form.layoutType === 'table' || filteredCompletions.some(c => c.isTableForm);

    if (isTableForm) {
      handleTableFormExportPDF(filteredCompletions, filterNonZero, filterColumn);
      return;
    }

    // STANDARD FORM PDF EXPORT (existing logic)
    // Get field labels in the order they appear in the form
    const formFieldLabels: string[] = [];
    if (form.fields && form.fields.length > 0) {
      // Sort by order field if available, otherwise use array order
      const sortedFields = [...form.fields].sort((a, b) => {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        return orderA - orderB;
      });
      formFieldLabels.push(...sortedFields.map(field => field.label));
    }

    // Collect any additional field labels from completions that might not be in form definition
    const allFieldLabels = new Set<string>(formFieldLabels);
    filteredCompletions.forEach(completion => {
      if (completion.fieldValues) {
        Object.keys(completion.fieldValues).forEach(label => {
          allFieldLabels.add(label);
        });
      }
    });

    // Create ordered field labels: form fields first (in order), then any extras
    const orderedFieldLabels = [
      ...formFieldLabels,
      ...Array.from(allFieldLabels).filter(label => !formFieldLabels.includes(label))
    ];

    const doc = new jsPDF('landscape'); // Use landscape for more columns
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    let yPos = margin;
    const lineHeight = 5;
    const fontSize = 7;

    // Helper to add a new page if needed
    const checkNewPage = (requiredHeight: number) => {
      if (yPos + requiredHeight > pageHeight - margin) {
        doc.addPage('landscape');
        yPos = margin;
      }
    };

    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Form Completion History', margin, yPos);
    yPos += lineHeight * 1.5;

    // Form name and date range
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const displayFormName = getFormName(form, preferredLanguage);
    doc.text(`Form: ${displayFormName}`, margin, yPos);
    const dateRangeText = `Date Range: ${startDate} to ${endDate}`;
    const dateRangeWidth = doc.getTextWidth(dateRangeText);
    doc.text(dateRangeText, pageWidth - margin - dateRangeWidth, yPos);
    yPos += lineHeight * 1.5;

    // Calculate column widths - only form fields
    const availableWidth = pageWidth - (margin * 2);
    const fieldColWidth = Math.max(20, availableWidth / orderedFieldLabels.length);

    // Table headers - only form field labels
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fontSize);
    const headers = orderedFieldLabels;
    const colWidths = Array(orderedFieldLabels.length).fill(fieldColWidth);

    let xPos = margin;
    headers.forEach((header, index) => {
      // Truncate long headers
      const maxHeaderWidth = colWidths[index] - 2;
      const headerText = doc.splitTextToSize(header, maxHeaderWidth);
      doc.text(headerText[0] || header.substring(0, 15), xPos, yPos);
      xPos += colWidths[index];
    });
    yPos += lineHeight;
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 3;

    // Helper to format date values - convert all date formats to consistent readable format
    const formatDateValue = (value: any): string => {
      if (!value) return '';
      
      if (typeof value === 'string') {
        // Check if it's a date string (plain YYYY-MM-DD or ISO timestamp)
        const isPlainDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
        const isIsoTimestamp = value.includes('T') || value.includes('Z');
        
        if (isPlainDate || isIsoTimestamp) {
          try {
            // Parse the date - for plain dates, append time to avoid timezone issues
            const dateString = isPlainDate ? `${value}T12:00:00` : value;
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
              // Convert to consistent readable format
              return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });
            }
          } catch (e) {
            // If parsing fails, return as is
          }
        }
      }
      
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      return String(value);
    };

    // Helper to format field value for PDF
    const formatFieldValue = (value: any): string => {
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'string') {
        // Check if it's a date string first
        const dateFormatted = formatDateValue(value);
        if (dateFormatted !== String(value)) {
          return dateFormatted;
        }
        // If it's a URL (photo), just show "[Photo]"
        if (value.startsWith('http://') || value.startsWith('https://')) {
          return '[Photo]';
        }
        return value;
      }
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return String(value);
    };

    // Table rows
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontSize);
    filteredCompletions.forEach((completion, index) => {
      checkNewPage(lineHeight * 3);

      // Build row data - only form field values
      const rowData = orderedFieldLabels.map(label => formatFieldValue(completion.fieldValues?.[label]));

      // Split text for each column and find max height
      const splitRowData = rowData.map((text, colIndex) => {
        const maxWidth = colWidths[colIndex] - 2;
        return doc.splitTextToSize(String(text), maxWidth);
      });

      const maxLines = Math.max(...splitRowData.map(text => text.length));
      const rowHeight = maxLines * lineHeight;

      xPos = margin;
      splitRowData.forEach((lines, colIndex) => {
        lines.forEach((line: string, lineIndex: number) => {
          doc.text(line, xPos, yPos + (lineIndex * lineHeight));
        });
        xPos += colWidths[colIndex];
      });

      yPos += rowHeight + 2;

      // Add separator line every 5 rows
      if ((index + 1) % 5 === 0 && index < filteredCompletions.length - 1) {
        doc.setLineWidth(0.2);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 3;
      }
    });

    // Footer
    yPos = pageHeight - margin;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Generated on ${new Date().toLocaleString()} - ${filteredCompletions.length} record(s)`,
      margin,
      yPos
    );

    // Save PDF (reuse displayFormName from line 806)
    doc.save(`${displayFormName || 'form'}_history_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // PDF Export for Table Forms
  const handleTableFormExportPDF = (completions: FormCompletion[], applyFilter: boolean = false, filterColumnName: string = '') => {
    // Get header field labels (like Date)
    const headerFieldLabels: string[] = [];
    // Header fields removed - use variables instead

    // Get table field labels (columns) in order
    const tableFieldLabels: string[] = [];
    if (form.tableFields && form.tableFields.length > 0) {
      const sortedTableFields = [...form.tableFields].sort((a, b) => {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        return orderA - orderB;
      });
      tableFieldLabels.push(...sortedTableFields.map(field => field.label));
    }

    // Collect all table field labels from completions (including calculated fields)
    const allTableFieldLabels = new Set<string>(tableFieldLabels);
    completions.forEach(completion => {
      if (completion.tableItems) {
        completion.tableItems.forEach(item => {
          const calculatedFields = calculateItemFields(item);
          Object.keys(calculatedFields).forEach(label => {
            allTableFieldLabels.add(label);
          });
        });
      }
    });

    // Create ordered headers: Date (from header), Item, then table fields
    const orderedTableFieldLabels = [
      ...tableFieldLabels,
      ...Array.from(allTableFieldLabels).filter(label => !tableFieldLabels.includes(label))
    ];

    // Find Date field label (common header field)
    const dateFieldLabel = headerFieldLabels.find(label => 
      label.toLowerCase().includes('date') || label.toLowerCase() === 'date'
    ) || (headerFieldLabels.length > 0 ? headerFieldLabels[0] : 'Date');

    // Build headers: Date, Item, then table fields
    const headers = [
      dateFieldLabel, // Date column
      'Item', // Item name column
      ...orderedTableFieldLabels // Table field columns
    ];

    const doc = new jsPDF('landscape'); // Use landscape for more columns
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    let yPos = margin;
    const lineHeight = 5;
    const fontSize = 7;

    // Helper to add a new page if needed
    const checkNewPage = (requiredHeight: number) => {
      if (yPos + requiredHeight > pageHeight - margin) {
        doc.addPage('landscape');
        yPos = margin;
      }
    };

    // Helper to format date values
    const formatDateValue = (value: any): string => {
      if (!value) return '';
      
      if (typeof value === 'string') {
        const isPlainDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
        const isIsoTimestamp = value.includes('T') || value.includes('Z');
        
        if (isPlainDate || isIsoTimestamp) {
          try {
            const dateString = isPlainDate ? `${value}T12:00:00` : value;
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
              return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });
            }
          } catch (e) {
            // If parsing fails, return as is
          }
        }
      }
      
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      return String(value);
    };

    // Helper to format field value for PDF
    const formatFieldValue = (value: any): string => {
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'string') {
        const dateFormatted = formatDateValue(value);
        if (dateFormatted !== String(value)) {
          return dateFormatted;
        }
        if (value.startsWith('http://') || value.startsWith('https://')) {
          return '[Photo]';
        }
        return value;
      }
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return String(value);
    };

    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Form Completion History (Table Form)', margin, yPos);
    yPos += lineHeight * 1.5;

    // Form name and date range
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const displayFormName = getFormName(form, preferredLanguage);
    doc.text(`Form: ${displayFormName}`, margin, yPos);
    const dateRangeText = `Date Range: ${startDate} to ${endDate}`;
    const dateRangeWidth = doc.getTextWidth(dateRangeText);
    doc.text(dateRangeText, pageWidth - margin - dateRangeWidth, yPos);
    yPos += lineHeight * 1.5;

    // Calculate column widths
    const availableWidth = pageWidth - (margin * 2);
    const colWidths = headers.map((header, index) => {
      // Date and Item columns get slightly less width, table fields share the rest
      if (index === 0) return Math.max(30, availableWidth * 0.12); // Date
      if (index === 1) return Math.max(40, availableWidth * 0.15); // Item
      return Math.max(25, (availableWidth * 0.73) / (headers.length - 2)); // Table fields
    });

    // Table headers
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fontSize);
    let xPos = margin;
    headers.forEach((header, index) => {
      const maxHeaderWidth = colWidths[index] - 2;
      const headerText = doc.splitTextToSize(header, maxHeaderWidth);
      doc.text(headerText[0] || header.substring(0, 20), xPos, yPos);
      xPos += colWidths[index];
    });
    yPos += lineHeight;
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 3;

    // Build rows: each tableItem becomes a row
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontSize);
    let rowIndex = 0;
    
    completions.forEach(completion => {
      // Get date from completionDate - use getDateFromValue helper for proper Firestore Timestamp handling
      const completionDateObj = getDateFromValue(completion.completionDate);
      const dateValue = completionDateObj ? completionDateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : '';

      if (completion.tableItems && completion.tableItems.length > 0) {
        completion.tableItems.forEach(tableItem => {
          // Apply filter if enabled: skip rows with N/A or empty values in selected column
          if (applyFilter && filterColumnName) {
            const calculatedFields = calculateItemFields(tableItem);
            const value = calculatedFields[filterColumnName];
            
            // Skip if value is N/A, empty, null, undefined, or zero (for numeric values)
            if (value === null || value === undefined || value === '' || value === 'N/A' || String(value).toUpperCase() === 'N/A') {
              return;
            }
            
            // For numeric values, also exclude zero
            const numValue = Number(value);
            if (!isNaN(numValue) && numValue === 0) {
              return;
            }
          }
          
          checkNewPage(lineHeight * 3);

          // Build row data: Date, Item, then table field values (including calculated fields)
          const calculatedFields = calculateItemFields(tableItem);
          const rowData: string[] = [];
          rowData.push(formatFieldValue(dateValue)); // Date column
          rowData.push(tableItem.item || ''); // Item column
          orderedTableFieldLabels.forEach(fieldLabel => {
            const value = calculatedFields[fieldLabel];
            rowData.push(formatFieldValue(value)); // Table field columns
          });

          // Split text for each column and find max height
          const splitRowData = rowData.map((text, colIndex) => {
            const maxWidth = colWidths[colIndex] - 2;
            return doc.splitTextToSize(String(text), maxWidth);
          });

          const maxLines = Math.max(...splitRowData.map(text => text.length));
          const rowHeight = maxLines * lineHeight;

          xPos = margin;
          splitRowData.forEach((lines, colIndex) => {
            lines.forEach((line: string, lineIndex: number) => {
              doc.text(line, xPos, yPos + (lineIndex * lineHeight));
            });
            xPos += colWidths[colIndex];
          });

          yPos += rowHeight + 2;

          // Add separator line every 5 rows
          if ((rowIndex + 1) % 5 === 0) {
            doc.setLineWidth(0.2);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 3;
          }
          rowIndex++;
        });
      }
    });

    // Footer
    yPos = pageHeight - margin;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Generated on ${new Date().toLocaleString()} - ${rowIndex} item record(s) from ${completions.length} completion(s)`,
      margin,
      yPos
    );

    // Save PDF (reuse displayFormName from line 1093)
    doc.save(`${displayFormName || 'form'}_table_history_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Get available filter columns for table forms
  const availableFilterColumns = useMemo(() => {
    const isTableForm = form.layoutType === 'table' || completions.some(c => c.isTableForm);
    if (!isTableForm) return [];
    
    const columns = new Set<string>();
    // Add table field labels
    if (form.tableFields) {
      form.tableFields.forEach(field => {
        columns.add(field.label);
      });
    }
    // Add any additional fields found in completions
    completions.forEach(completion => {
      if (completion.tableItems) {
        completion.tableItems.forEach(item => {
          const calculatedFields = calculateItemFields(item);
          Object.keys(calculatedFields).forEach(label => columns.add(label));
        });
      }
    });
    
    return Array.from(columns).sort();
  }, [form, completions]);

  const handleViewDetails = (completion: FormCompletion) => {
    // Check if this is a table form
    const isTableForm = completion.isTableForm || form.layoutType === 'table';
    
    if (isTableForm) {
      // For table forms, show tableItems data
      if (!completion.tableItems || completion.tableItems.length === 0) {
        alert('No form data available for this completion.');
        return;
      }
      
      // Build display content for table form
      let detailsContent = `<div style="font-family: Arial, sans-serif; padding: 20px;">`;
      const displayFormName = getFormName(form, preferredLanguage);
      detailsContent += `<h2 style="margin-bottom: 15px; color: #333;">${displayFormName || 'Form'} - Completion Details</h2>`;
      
      // Show completion metadata
      detailsContent += `<div style="margin-bottom: 20px; padding: 10px; background: #f5f5f5; border-radius: 5px;">`;
      if (completion.completionDate) {
        const completionDate = getDateFromValue(completion.completionDate);
        if (completionDate) {
          detailsContent += `<p style="margin: 5px 0;"><strong>Date:</strong> ${completionDate.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
          })}</p>`;
        }
      }
      if (completion.locationName) {
        detailsContent += `<p style="margin: 5px 0;"><strong>Location:</strong> ${completion.locationName}</p>`;
      }
      if (completion.completedBy) {
        detailsContent += `<p style="margin: 5px 0;"><strong>Completed By:</strong> ${completion.completedBy}</p>`;
      }
      detailsContent += `</div>`;
      
      // Show variables at the top (if any)
      if (completion.variableValues && Object.keys(completion.variableValues).length > 0) {
        detailsContent += `<div style="margin-bottom: 20px;">`;
        detailsContent += `<h3 style="margin-bottom: 10px; color: #555; font-size: 16px;">Variables</h3>`;
        detailsContent += `<table style="width: 100%; border-collapse: collapse; background: #fff; border: 1px solid #ddd; border-radius: 5px;">`;
        detailsContent += `<thead><tr style="background: #e0e0e0;">`;
        detailsContent += `<th style="padding: 8px; text-align: left; border: 1px solid #ddd; font-weight: bold;">Variable</th>`;
        detailsContent += `<th style="padding: 8px; text-align: left; border: 1px solid #ddd; font-weight: bold;">Value</th>`;
        detailsContent += `</tr></thead>`;
        detailsContent += `<tbody>`;
        Object.entries(completion.variableValues).forEach(([varName, varValue], idx) => {
          detailsContent += `<tr style="background: ${idx % 2 === 0 ? '#fff' : '#f9f9f9'};">`;
          detailsContent += `<td style="padding: 8px; border: 1px solid #ddd; font-weight: 500;">${varName}</td>`;
          detailsContent += `<td style="padding: 8px; border: 1px solid #ddd;">${varValue || 'N/A'}</td>`;
          detailsContent += `</tr>`;
        });
        detailsContent += `</tbody></table>`;
        detailsContent += `</div>`;
      }
      
      // Build column labels and row data for the view page (with filter/sort/export)
      const allFieldLabels = new Set<string>();
      completion.tableItems.forEach(item => {
        const calculatedFields = calculateItemFields(item);
        Object.keys(calculatedFields).forEach(label => allFieldLabels.add(label));
      });
      const fieldLabels = Array.from(allFieldLabels);
      const columns = ['Item', ...fieldLabels];

      let filteredItems = completion.tableItems;
      if (filterNonZero && filterColumn) {
        filteredItems = completion.tableItems.filter(item => {
          const calculatedFields = calculateItemFields(item);
          const value = calculatedFields[filterColumn];
          if (value === null || value === undefined || value === '' || value === 'N/A' || String(value).toUpperCase() === 'N/A') return false;
          const numValue = Number(value);
          if (!isNaN(numValue) && numValue === 0) return false;
          return true;
        });
      }

      const rowsData = filteredItems.map(item => {
        const calculatedFields = calculateItemFields(item);
        const row: Record<string, string> = { Item: item.item };
        fieldLabels.forEach(label => {
          const value = calculatedFields[label];
          row[label] = value !== null && value !== undefined ? String(value) : 'N/A';
        });
        return row;
      });

      const completionDateStr = (() => {
        const d = getDateFromValue(completion.completionDate);
        return d ? d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }) : '';
      })();

      const viewerPayload = {
        title: `${displayFormName || 'Form'} - Completion Details`,
        meta: { date: completionDateStr, location: completion.locationName || '', completedBy: completion.completedByName || completion.completedBy || '' },
        variables: completion.variableValues || {},
        columns,
        rows: rowsData
      };
      const safeJson = JSON.stringify(viewerPayload).replace(/<\/script>/gi, '\\u003c/script>');

      const fullDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${(viewerPayload.title || 'Form Completion').replace(/</g, '&lt;')}</title>
<style>
  body { font-family: Arial, sans-serif; padding: 16px; margin: 0; }
  .toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; padding: 10px; background: #f5f5f5; border-radius: 8px; flex-wrap: wrap; }
  .toolbar button { padding: 8px 14px; cursor: pointer; border: 1px solid #ccc; border-radius: 6px; background: #fff; font-size: 14px; }
  .toolbar button:hover { background: #e8e8e8; }
  .meta { margin-bottom: 16px; padding: 10px; background: #f0f0f0; border-radius: 6px; font-size: 14px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th, td { padding: 8px; text-align: left; border: 1px solid #ddd; }
  thead th { background: #e0e0e0; font-weight: bold; cursor: pointer; user-select: none; }
  thead th:hover { background: #d0d0d0; }
  thead th.sorted-asc::after { content: ' ▲'; font-size: 10px; }
  thead th.sorted-desc::after { content: ' ▼'; font-size: 10px; }
  .filter-row input, .filter-row select { width: 100%; padding: 4px 6px; font-size: 12px; box-sizing: border-box; }
  .filter-row th { background: #f0f0f0; padding: 4px; }
  tbody tr:nth-child(even) { background: #f9f9f9; }
  @media print { .toolbar, .filter-row, .no-print { display: none !important; } }
</style>
</head><body>
  <div class="toolbar no-print">
    <button type="button" id="btnPrint">Print</button>
    <button type="button" id="btnExportPdf">Export to PDF</button>
    <button type="button" id="btnExportExcel">Export to Excel</button>
  </div>
  <div class="meta no-print">
    <strong>Date:</strong> <span id="metaDate"></span> &nbsp;|&nbsp; <strong>Location:</strong> <span id="metaLocation"></span> &nbsp;|&nbsp; <strong>Completed by:</strong> <span id="metaBy"></span>
  </div>
  <div id="variablesSection"></div>
  <table id="dataTable">
    <thead>
      <tr id="headerRow"></tr>
      <tr class="filter-row no-print" id="filterRow"></tr>
    </thead>
    <tbody id="tbody"></tbody>
  </table>
  <script type="application/json" id="viewerData">${safeJson}</script>
  <script>
(function() {
  var data = JSON.parse(document.getElementById('viewerData').textContent);
  var columns = data.columns || [];
  var allRows = data.rows || [];
  var currentRows = allRows.slice();
  var sortCol = null;
  var sortDir = 1;
  var filters = {};

  function textVal(v) { return v == null ? '' : String(v); }
  function renderBody(rows) {
    var tbody = document.getElementById('tbody');
    tbody.innerHTML = '';
    rows.forEach(function(row, idx) {
      var tr = document.createElement('tr');
      tr.style.background = idx % 2 === 0 ? '#fff' : '#f9f9f9';
      columns.forEach(function(col) {
        var td = document.createElement('td');
        td.style.padding = '8px';
        td.style.border = '1px solid #ddd';
        if (col === 'Item') td.style.fontWeight = 'bold';
        td.textContent = textVal(row[col]);
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
  }
  function applyFiltersAndSort() {
    var filtered = allRows.filter(function(row) {
      for (var col in filters) {
        var f = (filters[col] || '').trim().toLowerCase();
        if (!f) continue;
        var cell = textVal(row[col]).toLowerCase();
        if (cell.indexOf(f) === -1) return false;
      }
      return true;
    });
    if (sortCol !== null) {
      filtered.sort(function(a, b) {
        var va = textVal(a[sortCol]);
        var vb = textVal(b[sortCol]);
        var na = Number(va);
        var nb = Number(vb);
        if (!isNaN(na) && !isNaN(nb)) return sortDir * (na - nb);
        return sortDir * va.localeCompare(vb);
      });
    }
    currentRows = filtered;
    renderBody(currentRows);
  }
  function buildHeader() {
    var headerRow = document.getElementById('headerRow');
    var filterRow = document.getElementById('filterRow');
    headerRow.innerHTML = '';
    filterRow.innerHTML = '';
    columns.forEach(function(col) {
      var th = document.createElement('th');
      th.textContent = col;
      th.addEventListener('click', function() {
        sortCol = col;
        sortDir = (sortCol === col && sortDir === 1) ? -1 : 1;
        document.querySelectorAll('#headerRow th').forEach(function(el) { el.classList.remove('sorted-asc', 'sorted-desc'); });
        th.classList.add(sortDir === 1 ? 'sorted-asc' : 'sorted-desc');
        applyFiltersAndSort();
      });
      headerRow.appendChild(th);
      var fth = document.createElement('th');
      var input = document.createElement('input');
      input.type = 'text';
      input.placeholder = 'Filter ' + col;
      input.addEventListener('input', function() {
        filters[col] = input.value;
        applyFiltersAndSort();
      });
      fth.appendChild(input);
      filterRow.appendChild(fth);
    });
  }
  function loadScript(src) {
    return new Promise(function(resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  function exportPdf() {
    loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js').then(function() {
      var doc = new window.jspdf.jsPDF('landscape');
      var margin = 10;
      var y = margin;
      var lineH = 5;
      var colW = (doc.internal.pageSize.getWidth() - 2 * margin) / columns.length;
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      columns.forEach(function(col, i) {
        doc.text(col.substring(0, 12), margin + i * colW, y);
      });
      y += lineH + 2;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      currentRows.forEach(function(row) {
        if (y > doc.internal.pageSize.getHeight() - 15) { doc.addPage('landscape'); y = margin; }
        columns.forEach(function(c, i) {
          var txt = textVal(row[c]).substring(0, 20);
          doc.text(txt, margin + i * colW, y);
        });
        y += lineH;
      });
      doc.save((data.title || 'form') + '_' + new Date().toISOString().slice(0,10) + '.pdf');
    }).catch(function() { alert('Could not load PDF library'); });
  }
  function exportExcel() {
    loadScript('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js').then(function() {
      var wsData = [columns].concat(currentRows.map(function(row) { return columns.map(function(c) { return row[c]; }); }));
      var ws = window.XLSX.utils.aoa_to_sheet(wsData);
      var wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, 'Completion');
      window.XLSX.writeFile(wb, (data.title || 'form') + '_' + new Date().toISOString().slice(0,10) + '.xlsx');
    }).catch(function() { alert('Could not load Excel library'); });
  }
  document.getElementById('metaDate').textContent = data.meta && data.meta.date ? data.meta.date : '';
  document.getElementById('metaLocation').textContent = data.meta && data.meta.location ? data.meta.location : '';
  document.getElementById('metaBy').textContent = data.meta && data.meta.completedBy ? data.meta.completedBy : '';
  if (data.variables && Object.keys(data.variables).length > 0) {
    var vs = document.getElementById('variablesSection');
    var rows = Object.keys(data.variables).map(function(k) {
      var v = data.variables[k];
      var val = v == null ? '' : String(v);
      return '<tr><td style="padding:6px 10px 6px 0;font-weight:600;color:#555;">' + escapeHtml(k) + '</td><td style="padding:6px 0;">' + escapeHtml(val) + '</td></tr>';
    }).join('');
    vs.innerHTML = '<div class="meta" style="margin-bottom:16px;"><strong>Variables</strong><table style="margin-top:8px;border-collapse:collapse;"><tbody>' + rows + '</tbody></table></div>';
  }
  function escapeHtml(s) {
    if (!s) return '';
    var div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }
  buildHeader();
  renderBody(currentRows);
  document.getElementById('btnPrint').onclick = function() { window.print(); };
  document.getElementById('btnExportPdf').onclick = exportPdf;
  document.getElementById('btnExportExcel').onclick = exportExcel;
})();
  </scr` + `ipt>
</body></html>`;

      const detailsWindow = window.open('', '_blank', 'width=1000,height=700');
      if (detailsWindow) {
        detailsWindow.document.write(fullDoc);
        detailsWindow.document.close();
      }
      return;
    }
    
    // Standard form - use existing logic
    const fieldValues = completion.fieldValues || {};
    const fieldEntries = Object.entries(fieldValues);
    
    if (fieldEntries.length === 0) {
      alert('No form data available for this completion.');
      return;
    }

    // Helper function to format date values in local time
    const formatValueForDisplay = (value: any): string => {
      if (value === null || value === undefined) {
        return 'N/A';
      }
      
      // Check if it's a date string (ISO format or date-like)
      if (typeof value === 'string') {
        // Check if it looks like a date string (ISO format with T or Z, or date format)
        const isoDatePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/;
        const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
        
        if (isoDatePattern.test(value) || dateOnlyPattern.test(value)) {
          try {
            const dateObj = new Date(value);
            if (!isNaN(dateObj.getTime())) {
              // Format as local date/time
              return dateObj.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short',
              });
            }
          } catch (e) {
            // If parsing fails, fall through to default string conversion
          }
        }
        
        // If it's a URL, return as is
        if (value.startsWith('http')) {
          return value;
        }
      }
      
      // Check if it's a Date object
      if (value instanceof Date) {
        if (isNaN(value.getTime())) {
          return 'Invalid Date';
        }
        return value.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short',
        });
      }
      
      // Check if it's an object (Firestore Timestamp or other)
      if (typeof value === 'object' && value !== null) {
        // Check if it's a Firestore Timestamp
        if (value.toDate && typeof value.toDate === 'function') {
          try {
            const dateObj = value.toDate();
            return dateObj.toLocaleString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZoneName: 'short',
            });
          } catch (e) {
            // If conversion fails, stringify
          }
        }
        return JSON.stringify(value, null, 2);
      }
      
      return String(value);
    };

    const completionDateStr = formatDate(getDateFromValue(completion.completionDate));
    const rowsForExport: [string, string][] = [
      ['Field', 'Value'],
      ['Completed At', completionDateStr],
      ...fieldEntries.map(([key, value]): [string, string] => [key, formatValueForDisplay(value)]),
      ...(completion.aiSummary ? ([['AI Summary', completion.aiSummary]] as [string, string][]) : []),
    ];

    // Show in a modal
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 class="text-xl font-semibold text-gray-900">Form Completion Details</h2>
          <button onclick="this.closest('.fixed').remove()" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="px-6 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-2">
          <button type="button" id="standardFormPrint" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Print</button>
          <button type="button" id="standardFormPdf" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Export to PDF</button>
          <button type="button" id="standardFormExcel" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Export to Excel</button>
        </div>
        <div class="px-6 py-4 overflow-y-auto flex-1">
          <div class="space-y-2">
            <div class="mb-4 pb-4 border-b border-gray-200">
              <div class="text-xs text-gray-500 mb-1">Completed At</div>
              <div class="text-sm font-medium text-gray-900">${completionDateStr}</div>
            </div>
            ${fieldEntries.map(([key, value]) => {
              const displayValue = formatValueForDisplay(value);
              
              // Special handling for photo URLs
              if (typeof value === 'string' && value.startsWith('http')) {
                return `<div class="mb-3">
                  <div class="text-sm font-medium text-gray-700 mb-1">${key}</div>
                  <img src="${value}" alt="${key}" class="max-w-full h-auto rounded border border-gray-300" />
                </div>`;
              }
              
              return `<div class="mb-2">
                <div class="text-sm font-medium text-gray-700">${key}</div>
                <div class="text-sm text-gray-900 mt-1 whitespace-pre-wrap">${displayValue}</div>
              </div>`;
            }).join('')}
            ${completion.aiSummary ? `
              <div class="mt-4 pt-4 border-t border-gray-200">
                <div class="text-sm font-medium text-gray-700 mb-1">AI Summary</div>
                <div class="text-sm text-gray-900 mt-1 whitespace-pre-wrap">${completion.aiSummary}</div>
              </div>
            ` : ''}
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button onclick="this.closest('.fixed').remove()" class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium">
            Close
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    const displayFormNameStd = getFormName(form, preferredLanguage);
    const printTitle = `${displayFormNameStd || 'Form'} - Completion`;

    const printBtn = modal.querySelector('#standardFormPrint');
    const pdfBtn = modal.querySelector('#standardFormPdf');
    const excelBtn = modal.querySelector('#standardFormExcel');
    if (printBtn) {
      printBtn.addEventListener('click', () => {
        const printHtml = `<!DOCTYPE html><html><head><title>${printTitle.replace(/</g, '&lt;')}</title><style>body{font-family:Arial,sans-serif;padding:20px;} table{border-collapse:collapse;width:100%;} th,td{border:1px solid #ddd;padding:8px;text-align:left;} th{background:#f0f0f0;}</style></head><body><h2>${printTitle.replace(/</g, '&lt;')}</h2><p><strong>Completed:</strong> ${completionDateStr}</p><table><thead><tr><th>Field</th><th>Value</th></tr></thead><tbody>${rowsForExport.slice(1).map(([k, v]) => `<tr><td>${String(k).replace(/</g, '&lt;')}</td><td>${String(v).replace(/</g, '&lt;').replace(/\n/g, '<br>')}</td></tr>`).join('')}</tbody></table></body></html>`;
        const w = window.open('', '_blank');
        if (w) {
          w.document.write(printHtml);
          w.document.close();
          w.focus();
          setTimeout(() => { w.print(); }, 250);
        }
      });
    }
    if (pdfBtn) {
      pdfBtn.addEventListener('click', () => {
        const doc = new jsPDF();
        let y = 10;
        doc.setFontSize(14);
        doc.text(printTitle, 10, y);
        y += 10;
        doc.setFontSize(10);
        doc.text(`Completed: ${completionDateStr}`, 10, y);
        y += 8;
        rowsForExport.slice(1).forEach(([key, val]) => {
          if (y > 270) { doc.addPage(); y = 10; }
          doc.setFont('helvetica', 'bold');
          doc.text(String(key), 10, y);
          doc.setFont('helvetica', 'normal');
          const lines = doc.splitTextToSize(String(val), 180);
          doc.text(lines, 20, y + 5);
          y += 5 + (lines.length * 5);
        });
        doc.save(`${(displayFormNameStd || 'form').replace(/[^a-z0-9]/gi, '_')}_completion_${new Date().toISOString().split('T')[0]}.pdf`);
      });
    }
    if (excelBtn) {
      excelBtn.addEventListener('click', () => {
        const escapeCsv = (s: string) => (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${String(s).replace(/"/g, '""')}"` : s;
        const csv = rowsForExport.map(row => row.map(cell => escapeCsv(String(cell))).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${(displayFormNameStd || 'form').replace(/[^a-z0-9]/gi, '_')}_completion_${new Date().toISOString().split('T')[0]}.csv`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      });
    }
    
    // Close on outside click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Form History</h2>
            <p className="text-sm text-gray-500 mt-1">{getFormName(form, preferredLanguage)}</p>
          </div>
          <div className="flex items-center gap-3">
            {filteredCompletions.length > 0 && (
              <>
                <button
                  onClick={handleExportExcel}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  title="Export to Excel (CSV)"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Export Excel
                </button>
                <button
                  onClick={handleExportPDF}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  title="Export to PDF"
                >
                  <FileText className="w-4 h-4" />
                  Export PDF
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Start Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* End Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Location Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                Location
              </label>
              <select
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">All Locations</option>
                {availableLocations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Table Form Filter: Non-Zero/Non-N/A Filter */}
          {(form.layoutType === 'table' || filteredCompletions.some(c => c.isTableForm)) && availableFilterColumns.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <input
                  type="checkbox"
                  checked={filterNonZero}
                  onChange={(e) => {
                    setFilterNonZero(e.target.checked);
                    if (!e.target.checked) {
                      setFilterColumn('');
                    }
                  }}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Filter out rows with N/A or empty values
              </label>
              {filterNonZero && (
                <select
                  value={filterColumn}
                  onChange={(e) => setFilterColumn(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select column to filter by...</option>
                  {availableFilterColumns.map(column => (
                    <option key={column} value={column}>
                      {column}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Clear Filters Button */}
          {(startDate || endDate || selectedLocationId || (filterNonZero && filterColumn)) && (
            <div className="mt-3">
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setSelectedLocationId('');
                  setFilterNonZero(false);
                  setFilterColumn('');
                }}
                className="text-sm text-purple-600 hover:text-purple-800"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading history...</div>
            </div>
          ) : filteredCompletions.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500 text-center">
                <p className="font-medium">No completions found</p>
                <p className="text-sm mt-1">
                  {completions.length === 0
                    ? 'This form has not been completed yet.'
                    : 'No completions match the selected filters.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                      Completed At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                      Completed By
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                      Asset Name
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 border border-gray-200">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompletions.map((completion, index) => {
                    const completionDate = getDateFromValue(completion.completionDate);
                    const location = completion.locationId ? locations[completion.locationId] : null;
                    const assetName = completion.assetId ? assetNames[completion.assetId] : '';

                    return (
                      <tr
                        key={completion.id || index}
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}
                      >
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-900">
                            {formatDate(completionDate)}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {location ? location.name : completion.locationName || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {completion.completedByName || completion.completedBy || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {assetName || ''}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleViewDetails(completion)}
                              className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {filteredCompletions.length} of {completions.length} completion{completions.length !== 1 ? 's' : ''}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormHistory;

